import { cleanUndefinedKeysRecurse } from '../../lib/util';
import Deliverable from './Deliverable';
import { deliverableTypes } from './deliverableTypes';

/**
 * @typedef {import('./Deliverable').DeliverableData & ChildClassData} PublicTourData
 * @combinedata
 */
/**
 * @typedef {Object} ChildClassData
 * @property {number} _publicTourDataVer
 * @property {string} url
 * @ignore
 */

const latestDataVer = 1;

export default class PublicTour extends Deliverable {
    static _targetType = deliverableTypes.publicTour;

    /**
     * @param {PublicTourData} inputData 
     */
    constructor(inputData) {
        super(inputData);
    }

    /**
     * @param {PublicTourData} inputData 
     */
    _reInit(inputData) {
        super._reInit(inputData);
        this._publicTourDataVer = inputData._publicTourDataVer || latestDataVer;

        if (this._publicTourDataVer !== latestDataVer) { throw new Error(`Metadata version not latest: Got ${this._publicTourDataVer}, should be ${latestDataVer}`); }

        this.url = inputData.url;
        /** @type {string|undefined} YYYY-MM-DD, chosen by the artist in Mercury */
        this.tourDate = inputData.tourDate || undefined;
    }

    /**
     * The date to show on the card: the tour date, else when the link was added to Sizzle.
     * A YYYY-MM-DD string is read as a LOCAL date: `new Date('2026-06-09')` is UTC midnight, which shows as
     * the 8th anywhere west of UTC.
     * @returns {Date}
     */
    get displayDate() {
        const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(this.tourDate || '');
        return m ? new Date(+m[1], +m[2] - 1, +m[3]) : this.creationDate;
    }

    serialize() {
        const ser = {
            ...super.serialize(),
            _publicTourDataVer: this._publicTourDataVer,
            url: this.url,
            tourDate: this.tourDate,
        };

        return cleanUndefinedKeysRecurse(ser);
    }

    getUpdatableData() {
        // we can only request to directly update these
        const updates = {
            ...super.getUpdatableData(),
            _publicTourDataVer: this._publicTourDataVer, // even though we can't technically update this, send it so the backend can throw if we are sending the wrong data ver
            url: this.url,
            tourDate: this.tourDate,
        };

        return cleanUndefinedKeysRecurse(updates);
    }
}

export const EMBED_TOUR_HOST = 'https://tours.atlasbayvr.com/';

/**
 * Checks and tidies an Embeddable 360 Tour URL. Keep in step with checkEmbedTourUrl in
 * Backend/services/metadata/deliverables/PublicTour.js, which enforces the same rules on save.
 *
 * Rules [Hope, 2026-11-04]: on https://tours.atlasbayvr.com/, all lowercase, ending in `/`.
 * `allowCapitals` lets Mercury inspect a capitalised URL before deciding whether it may lowercase it.
 *
 * @param {string} rawUrl
 * @returns {{ url?: string, error?: string }}
 */
export function checkEmbedTourUrl(rawUrl, { allowCapitals = false } = {}) {
    if (typeof rawUrl !== 'string' || !rawUrl.trim()) return { error: 'A tour link is required.' };
    let url = rawUrl.trim();
    if (/\s/.test(url)) return { error: 'A tour link cannot contain spaces.' };
    if (!url.toLowerCase().startsWith(EMBED_TOUR_HOST)) return { error: `Tour links must start with ${EMBED_TOUR_HOST}` };
    url = EMBED_TOUR_HOST + url.slice(EMBED_TOUR_HOST.length);
    if (/[?#]/.test(url)) return { error: 'A tour link cannot include ? or # parts.' };
    if (url === EMBED_TOUR_HOST) return { error: 'A tour link must point at a tour folder, not the site root.' };
    if (!allowCapitals && /[A-Z]/.test(url)) return { error: 'Tour links must be all lowercase.' };
    if (!/^[a-z0-9\-._~/%]+$/i.test(url.slice(EMBED_TOUR_HOST.length))) return { error: 'A tour link can only contain letters, numbers, - _ . ~ and /' };
    const lastSegment = url.slice(url.lastIndexOf('/') + 1);
    if (lastSegment && !lastSegment.includes('.')) url += '/';
    return { url };
}